import React, { useState } from 'react';
import { formatBytes } from '../../utils/formatBytes';
import { getFileIcon, getFileTypeLabel } from '../../utils/fileIcon';
import { PreviewModal } from './PreviewModal';

/**
 * Get status badge color and label
 */
function getStatusBadge(status) {
  switch (status) {
    case 'pending':
      return { color: 'bg-yellow-100 text-yellow-800', label: 'Processing', icon: '⏳' };
    case 'clean':
      return { color: 'bg-green-100 text-green-800', label: 'Available', icon: '✓' };
    case 'deleted':
      return { color: 'bg-gray-100 text-gray-800', label: 'Deleted', icon: '🗑️' };
    default:
      return { color: 'bg-gray-100 text-gray-800', label: status, icon: '•' };
  }
}

/**
 * Format date for display
 */
function formatDate(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString();
}

/**
 * AttachmentsList Component
 * Display files with preview/download/delete actions
 */
export function AttachmentsList({
  files = [],
  isLoading = false,
  error = null,
  currentUserId = null,
  currentUserRole = null,
  hasProcessingFiles = false,
  downloadFileBlob,
  deleteFile,
  fetchPreviewBlob,
  onRefresh,
}) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Handle delete
  const handleDelete = async (file) => {
    if (!window.confirm(`Delete "${file.originalName}"?`)) {
      return;
    }

    setDeletingId(file.id);
    try {
      await deleteFile(file.id);
    } catch (err) {
      alert(`Failed to delete file: ${err.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  // Handle download
  const handleDownload = async (file) => {
    try {
      await downloadFileBlob(file.id, file.originalName);
    } catch (err) {
      alert(`Failed to download file: ${err.message}`);
    }
  };

  // Check if user can delete file
  const canDelete = (file) => {
    return (
      currentUserId === file.uploadedBy ||
      currentUserRole === 'admin' ||
      currentUserRole === 'gm'
    );
  };

  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-start gap-3">
          <div className="text-2xl">⚠️</div>
          <div>
            <h3 className="text-lg font-semibold text-red-900 mb-1">Error Loading Files</h3>
            <p className="text-sm text-red-700">{error}</p>
            {onRefresh && (
              <button
                onClick={onRefresh}
                className="mt-3 text-sm text-red-700 hover:text-red-800 underline"
              >
                Try Again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (isLoading && files.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-600">Loading files...</p>
        </div>
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div className="p-8 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg text-center">
        <div className="text-4xl mb-3">📎</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Files Yet</h3>
        <p className="text-sm text-gray-600">
          Files uploaded by you or the other party will appear here.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {/* Processing indicator */}
        {hasProcessingFiles && (
          <div 
            className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm"
            role="status"
            aria-live="polite"
          >
            <div className="w-4 h-4 border-2 border-yellow-600 border-t-transparent rounded-full animate-spin" />
            <span className="text-yellow-800">Some files are still processing...</span>
          </div>
        )}

        {/* Files grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {files.map(file => {
            const statusBadge = getStatusBadge(file.status);
            const isDeleting = deletingId === file.id;
            const isClean = file.status === 'clean';

            return (
              <div
                key={file.id}
                className="flex flex-col p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
              >
                {/* File icon and type */}
                <div className="flex items-start gap-3 mb-3">
                  <div className="text-3xl flex-shrink-0">
                    {getFileIcon(file.mimeType)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 
                      className="font-medium text-gray-900 truncate"
                      title={file.originalName}
                    >
                      {file.originalName}
                    </h4>
                    <p className="text-xs text-gray-500">
                      {getFileTypeLabel(file.mimeType)} • {formatBytes(file.size)}
                    </p>
                  </div>
                </div>

                {/* Status badge */}
                <div className="mb-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${statusBadge.color}`}>
                    <span>{statusBadge.icon}</span>
                    {statusBadge.label}
                  </span>
                </div>

                {/* Metadata */}
                <div className="text-xs text-gray-500 mb-4 space-y-1">
                  <p>Uploaded by: <span className="font-medium">{file.role}</span></p>
                  <p>{formatDate(file.createdAt)}</p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 mt-auto">
                  {/* Preview button */}
                  {isClean && file.previewAvailable && (
                    <button
                      onClick={() => setSelectedFile(file)}
                      className="flex-1 px-3 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
                      aria-label={`Preview ${file.originalName}`}
                    >
                      👁️ Preview
                    </button>
                  )}

                  {/* Download button */}
                  {isClean && (
                    <button
                      onClick={() => handleDownload(file)}
                      className="flex-1 px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                      aria-label={`Download ${file.originalName}`}
                    >
                      ⬇️ Download
                    </button>
                  )}

                  {/* Delete button */}
                  {canDelete(file) && (
                    <button
                      onClick={() => handleDelete(file)}
                      disabled={isDeleting}
                      className="px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label={`Delete ${file.originalName}`}
                    >
                      {isDeleting ? '...' : '🗑️'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Preview Modal */}
      {selectedFile && (
        <PreviewModal
          file={selectedFile}
          fetchPreviewBlob={fetchPreviewBlob}
          onClose={() => setSelectedFile(null)}
        />
      )}
    </>
  );
}
